import React, { useState } from 'react';
import { Button, Card, Col, Form, Input, Row, Select, Space, Table, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  OiEmptyState,
  OiPageFrame,
  PrimaryKpiCard,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { automationService } from '@/services/automation.service';

const { Title, Paragraph, Text } = Typography;

const AutomationMonitorPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [eventType, setEventType] = useState('supplier_delay');

  const monitorQ = useQuery({
    queryKey: ['eabp-monitor'],
    queryFn: () => automationService.getMonitor(),
    staleTime: 30_000,
  });
  const runtimeQ = useQuery({
    queryKey: ['eabp-runtime'],
    queryFn: () => automationService.getRuntime(),
    staleTime: 60_000,
  });

  const emitMut = useMutation({
    mutationFn: () =>
      automationService.emitEvent({
        eventType,
        idempotencyKey: `ui:${eventType}:${Date.now()}`,
        payload: { severity: 'high', source: 'manual_ui' },
      }),
    onSuccess: () => {
      message.success('Business event emitted — playbooks may start');
      void qc.invalidateQueries({ queryKey: ['eabp-monitor'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const dash = monitorQ.data || {};
  const recent = (dash.recent as any[]) || [];

  return (
    <OiPageFrame
      subtitle="Enterprise automation coordinates processes — it never posts journals or approves payments/POs outside the approval framework."
      loading={monitorQ.isLoading && !monitorQ.data}
      error={Boolean(monitorQ.error)}
      errorMessage="Automation monitor unavailable"
    >
      <Title level={3} style={{ marginTop: 0 }}>
        Enterprise Automation
      </Title>
      {(dash as any).degraded || (runtimeQ.data as any)?.disabled ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          EABP degraded or disabled (`EABP_JOBS_DISABLED=1`).
        </Card>
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard label="Running" value={(dash as any).running ?? '—'} />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard label="Waiting" value={(dash as any).waiting ?? '—'} />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard label="Failed" value={(dash as any).failed ?? '—'} />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard label="Succeeded" value={(dash as any).succeeded ?? '—'} />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard
            label="Hours saved (proxy)"
            value={(dash as any).hoursSavedProxy ?? '—'}
          />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <PrimaryKpiCard
            label="Avg cycle (min)"
            value={(dash as any).avgCycleMinutes ?? '—'}
          />
        </Col>
      </Row>

      <Card title="Start from business event" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            style={{ width: 220 }}
            value={eventType}
            onChange={setEventType}
            options={[
              { value: 'supplier_delay', label: 'Supplier Delay' },
              { value: 'invoice_overdue', label: 'Invoice Overdue' },
              { value: 'machine_down', label: 'Machine Down' },
              { value: 'inspection_failed', label: 'Inspection Failed' },
            ]}
          />
          <Button type="primary" loading={emitMut.isPending} onClick={() => emitMut.mutate()}>
            Emit & orchestrate
          </Button>
          <Button onClick={() => navigate('/owner-intelligence/automation/designer')}>
            Designer
          </Button>
        </Space>
      </Card>

      <Card title="Recent executions">
        {recent.length === 0 ? (
          <OiEmptyState title="No executions yet" body="Emit a business event or accept an EOIP supplier-delay recommendation." />
        ) : (
          <Table
            rowKey={(r) => String(r.id)}
            dataSource={recent}
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Playbook',
                render: (_, r) => r.playbook_title || r.playbook_code || '—',
              },
              {
                title: 'Status',
                dataIndex: 'status',
                render: (s: string) => <Tag>{s}</Tag>,
              },
              { title: 'Current node', dataIndex: 'current_node_key' },
              { title: 'Started', dataIndex: 'started_at' },
              {
                title: '',
                render: (_, r) => (
                  <Button
                    type="link"
                    onClick={() => navigate(`/owner-intelligence/automation/executions/${r.id}`)}
                  >
                    Open
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>
      <Paragraph type="secondary" style={{ marginTop: 12 }}>
        <Text type="secondary">
          Connectors: {((runtimeQ.data as any)?.connectors || []).filter((c: any) => c.enabled).map((c: any) => c.channel).join(', ') || '—'}
        </Text>
      </Paragraph>
    </OiPageFrame>
  );
};

export default AutomationMonitorPage;
